import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, Col, FormGroup, Label, Row } from 'reactstrap';
import { AvGroup, AvInput } from 'availity-reactstrap-validation';
import { MmamaEntityType } from 'app/shared/model/enumerations/mmama-entity-type.model';
import { IMmamaEntityDetail } from 'app/shared/model/mmama-entity-detail.model';
import { IMmamaEntityMaster } from 'app/shared/model/mmama-entity-master.model';
import { IMmamaEntityDto } from 'app/shared/model/mmama-entity-dto.model';
import { useSelector } from 'react-redux';

interface MmamaEntityFormGroupProps {
  prefix: string;
  entities: IMmamaEntityMaster[];
  initialValues: IMmamaEntityDto[];
  onChange: (data: IMmamaEntityDto[]) => void;
  disabled?: boolean;
  mergeAndSort?: boolean;
  entityOrder?: MmamaEntityType[];
  onValidationChange?: (missingLabels: string[]) => void;
  showValidation?: boolean;
}

interface MissingItem {
  key: string; // detail id for text / multi-select details, master id for dropdowns and group level checks
  label: string;
  message: string;
}

interface FlattenedEntity {
  master: IMmamaEntityMaster;
  detail: IMmamaEntityDetail;
  displayOrder: number;
}

const MmamaEntityFormGroup = (props: MmamaEntityFormGroupProps) => {
  const { entities, initialValues, onChange, disabled, prefix, mergeAndSort, entityOrder, onValidationChange, showValidation } = props;
  const currentLocale = 'en'; // For future localization support

  const [multiSelectEntityIds, setMultiSelectEntityIds] = useState<{ [key: string]: boolean }>({});
  const [dropDownEntityIds, setDropDownEntityIds] = useState<{ [key: string]: string }>({});
  const [dropDownEntityComments, setDropDownEntityComments] = useState<{ [key: string]: string }>({});
  const [textEntityComments, setTextEntityComments] = useState<{ [key: string]: string }>({});


  const defaultEntityOrder = [
    MmamaEntityType.HEADING,
    MmamaEntityType.MULTISELECT,
    MmamaEntityType.DROPDOWN,
    MmamaEntityType.TEXT
  ];

  const missingItems = React.useMemo(() => {
    const missing: MissingItem[] = [];
    (entities || []).forEach(master => {
      if (master.type === MmamaEntityType.HEADING) return;
      const details = (master.mmamaEntityDetails || []).filter(detail => detail.isActive);
      if (!details.length) return;

      if (master.type === MmamaEntityType.DROPDOWN) {
        if (master.required && !dropDownEntityIds[master.id]) {
          missing.push({ key: master.id, label: master.entityName, message: 'This field is required.' });
        }
        return;
      }

      const isFilled = (detail: IMmamaEntityDetail) =>
        master.type === MmamaEntityType.MULTISELECT
          ? multiSelectEntityIds[detail.id] !== undefined
          : (textEntityComments[detail.id] || '').trim() !== '';
      details.forEach(detail => {
        if (detail.required && !isFilled(detail)) {
          missing.push({
            key: detail.id,
            label: detail.enDescription,
            message: master.type === MmamaEntityType.MULTISELECT ? 'Please select Yes or No.' : 'This field is required.'
          });
        }
      });
    });
    return missing;
  }, [entities, multiSelectEntityIds, dropDownEntityIds, textEntityComments]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(missingItems.map(item => item.label));
    }
  }, [missingItems]);

  const requiredMark = (required: boolean) => (required ? <span className="text-danger"> *</span> : null);

  const renderError = (key: string) => {
    const item = showValidation ? missingItems.find(m => m.key === key) : null;
    return item ? <div className="text-danger small">{item.message}</div> : null;
  };

  // Flatten and sort entities based on both master and detail display orders
  const sortedFlattenedEntities = React.useMemo(() => {
    if (!entities) return [];

    const flattened: FlattenedEntity[] = [];

    // First sort master entities by display order
    const sortedMasters = [...entities].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    // For each master entity, process its details
    sortedMasters.forEach(master => {
      if (!master.mmamaEntityDetails?.length) return;

      // For DROPDOWN type, use master's display order
      if (master.type === MmamaEntityType.DROPDOWN) {
        flattened.push({
          master,
          detail: master.mmamaEntityDetails[0], // For dropdowns, we only need one entry
          displayOrder: master.displayOrder || 0
        });
      } else {
        // For other types, use detail's display order
        master.mmamaEntityDetails
          .filter(detail => detail.isActive)
          .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
          .forEach(detail => {
            flattened.push({
              master,
              detail,
              displayOrder: detail.displayOrder || 0
            });
          });
      }
    });

    // Sort the flattened array by display order
    return flattened.sort((a, b) => a.displayOrder - b.displayOrder);
  }, [entities]);

  useEffect(() => {
    if (!initialValues || !initialValues.length || !entities || !entities.length) {
      return;
    }
    const initialMultiSelectEntityIds: { [key: string]: boolean } = {};
    const initialDropDownEntityIds: { [key: string]: string } = {};
    const initialDropDownEntityComments: { [key: string]: string } = {};
    const initialTextEntityComments: { [key: string]: string } = {};

    initialValues.forEach(value => {
      if (value.entityDetail) {
        const detail = value.entityDetail;
        if (detail.entityMaster?.type === MmamaEntityType.MULTISELECT) {
          if (typeof value.value === 'boolean') {
            initialMultiSelectEntityIds[detail.id] = value.value;
          }
        } else if (detail.entityMaster?.type === MmamaEntityType.DROPDOWN) {
          initialDropDownEntityIds[detail.entityMaster?.id ?? detail.entityMasterId as any] = detail.id;
          initialDropDownEntityComments[detail.id] = value.comment ?? '';
        } else if (detail.entityMaster?.type === MmamaEntityType.TEXT) {
          initialTextEntityComments[detail.id] = value.comment ?? '';
        }
      }
    });

    setMultiSelectEntityIds(initialMultiSelectEntityIds);
    setDropDownEntityIds(initialDropDownEntityIds);
    setDropDownEntityComments(initialDropDownEntityComments);
    setTextEntityComments(initialTextEntityComments);
  }, [entities, initialValues]);

  useEffect(() => {
    let postData = [];

    // set values for multi select entities
    postData = Object.keys(multiSelectEntityIds)
      .filter(key => typeof multiSelectEntityIds[key] === 'boolean')
      .map(entityDetailId => ({
        entityDetailId,
        comment: null,
        value: multiSelectEntityIds[entityDetailId]
      }));

    // set values for dropdown entities
    Object.keys(dropDownEntityIds).forEach(key => {
      postData.push({
        entityDetailId: dropDownEntityIds[key],
        comment: dropDownEntityComments[dropDownEntityIds[key]] || null
      });
    });

    // set values for text entities
    Object.keys(textEntityComments).forEach(entityDetailId => {
      postData.push({
        entityDetailId,
        comment: textEntityComments[entityDetailId]
      });
    });

    onChange(postData);
  }, [multiSelectEntityIds, dropDownEntityIds, dropDownEntityComments, textEntityComments]);

  const handleMultiSelectChange = (id: string, value: boolean) => {
    const { [id]: current, ...others } = multiSelectEntityIds;
    // pressing the selected button again clears the answer
    setMultiSelectEntityIds(current === value ? others : { ...others, [id]: value });
  };

  const handleDropdownChange = (id: string, value: string) => {
    setDropDownEntityIds({
      ...dropDownEntityIds,
      [id]: value
    });
  };

  const handleDropdownCommentChange = (id: string, value: string) => {
    setDropDownEntityComments({
      ...dropDownEntityComments,
      [id]: value
    });
  };

  const handleTextChange = (id: string, value: string) => {
    setTextEntityComments({
      ...textEntityComments,
      [id]: value
    });
  };

  const renderHeading = (detail: IMmamaEntityDetail) => (
    <Col md="12" key={detail.id} className="mt-3">
      {/* <h5 className="text-primary"> */}
      <div className="mb-2">
        {currentLocale !== 'en' && detail.stDescription ? detail.stDescription : detail.enDescription}
      </div>
      {/* </h5> */}
    </Col>
  );

  const renderMultiSelect = (detail: IMmamaEntityDetail) => (
    <Col md="12" key={detail.id} className="mt-3">
      <FormGroup>
        <Label>
          {currentLocale !== 'en' && detail.stDescription ? detail.stDescription : detail.enDescription}
          {requiredMark(!!detail.required)}
        </Label>
        <ButtonGroup style={{ marginLeft: '30px' }}>
          <Button
            color={multiSelectEntityIds[detail.id] === true ? 'primary' : 'secondary'}
            onClick={() => handleMultiSelectChange(detail.id, true)}
            disabled={disabled}
          >
            Yes
          </Button>
          <Button
            color={multiSelectEntityIds[detail.id] === false ? 'primary' : 'secondary'}
            onClick={() => handleMultiSelectChange(detail.id, false)}
            disabled={disabled}
          >
            No
          </Button>
        </ButtonGroup>
        {renderError(detail.id)}
      </FormGroup>
    </Col>
  );

  const renderDropdown = (master: IMmamaEntityMaster) => (
    <>
      <Col md="6" className="mt-3" key={master.id}>
        <AvGroup>
          <Label id={master.id} for={`${prefix}-select-${master.id}`}>
            {currentLocale !== 'en' && master.stEntityName ? master.stEntityName : master.entityName}
            {requiredMark(!!master.required)}
          </Label>
          <AvInput
            id={`${prefix}-select-${master.id}`}
            type="select"
            disabled={disabled}
            className="form-control"
            name={`${prefix}-select-${master.id}`}
            value={dropDownEntityIds[master.id] || ''}
            onChange={(e) => handleDropdownChange(master.id, e.target.value)}
            validate={{ required: { value: !!master.required, errorMessage: 'This field is required.' } }}
          >
            <option disabled selected value="">select an option</option>
            {(master.mmamaEntityDetails || [])
              .filter(detail => detail.isActive)
              .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
              .map((detail: IMmamaEntityDetail) => (
                <option value={detail.id} key={detail.id}>
                  {currentLocale !== 'en' && detail.stDescription ? detail.stDescription : detail.enDescription}
                </option>
              ))}
          </AvInput>
          {renderError(master.id)}
        </AvGroup>
      </Col>
      {(master.mmamaEntityDetails || []).find((detail: IMmamaEntityDetail) => detail.id === dropDownEntityIds[master.id])?.displayOrder === 99 && (
        <Col md="6" className="mt-3">
          <AvGroup>
            <Label id={`${prefix}-other-label`}
                   for={`${prefix}-other-comment-${dropDownEntityIds[master.id]}`}>
              If Other, Please Specify
            </Label>
            <AvInput
              id={`${prefix}-other-comment-${dropDownEntityIds[master.id]}`}
              type="text"
              name={`${prefix}-other-comment-${dropDownEntityIds[master.id]}`}
              value={dropDownEntityComments[dropDownEntityIds[master.id]] || ''}
              disabled={disabled}
              onChange={(e) => handleDropdownCommentChange(dropDownEntityIds[master.id], e.target.value)}
              validate={{
                required: { value: true, errorMessage: 'This field is required.' }
              }}
            />
          </AvGroup>
        </Col>
      )}
    </>
  );

  const renderText = (detail: IMmamaEntityDetail) => (
    <Col md="6" key={detail.id} className="mt-3">
      <AvGroup>
        <Label id={detail.id} for={`${prefix}-comment-${detail.id}`}>
          {currentLocale !== 'en' && detail.stDescription ? detail.stDescription : detail.enDescription}
          {requiredMark(!!detail.required)}
        </Label>
        <AvInput
          id={`${prefix}-comment-${detail.id}`}
          type="text"
          className="form-control"
          name={`${prefix}-comment-${detail.id}`}
          value={textEntityComments[detail.id] || ''}
          disabled={disabled}
          onChange={(e) => handleTextChange(detail.id, e.target.value)}
        />
        {renderError(detail.id)}
      </AvGroup>
    </Col>
  );

  const renderEntity = (flattenedEntity: FlattenedEntity) => {
    const { master, detail: currentDetail } = flattenedEntity;

    switch (master.type) {
      case MmamaEntityType.HEADING:
        return renderHeading(currentDetail);
      case MmamaEntityType.MULTISELECT:
        return renderMultiSelect(currentDetail);
      case MmamaEntityType.DROPDOWN:
        return renderDropdown(master);
      case MmamaEntityType.TEXT:
        return renderText(currentDetail);
      default:
        return null;
    }
  };

  const renderEntityByType = (type: MmamaEntityType) => {
    if (!entities || !entities.length) {
      return null;
    }

    switch (type) {
      case MmamaEntityType.HEADING:
        return (
          <Row className="justify-content-left">
            {entities
              .filter(entity => entity.type === MmamaEntityType.HEADING)
              .map(entity =>
                (entity.mmamaEntityDetails || [])
                  .filter(detail => detail.isActive)
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .map(detail => renderHeading(detail))
              )}
          </Row>
        );
      case MmamaEntityType.MULTISELECT:
        return (
          <Row className="justify-content-left">
            {entities
              .filter(entity => entity.type === MmamaEntityType.MULTISELECT)
              .map(entity =>
                (entity.mmamaEntityDetails || [])
                  .filter(detail => detail.isActive)
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .map(detail => renderMultiSelect(detail))
              )}
          </Row>
        );
      case MmamaEntityType.DROPDOWN:
        return (
          <Row className="justify-content-left">
            {entities
              .filter(entity => entity.type === MmamaEntityType.DROPDOWN)
              .map(entity => renderDropdown(entity))}
          </Row>
        );
      case MmamaEntityType.TEXT:
        return (
          <Row className="justify-content-left">
            {entities
              .filter(entity => entity.type === MmamaEntityType.TEXT)
              .map(entity =>
                (entity.mmamaEntityDetails || [])
                  .filter(detail => detail.isActive)
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .map(detail => renderText(detail))
              )}
          </Row>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {mergeAndSort
        ? (
          <Row className="justify-content-left">
            {sortedFlattenedEntities.map(flattenedEntity => renderEntity(flattenedEntity))}
          </Row>
        )
        : (entityOrder || defaultEntityOrder).map(type => renderEntityByType(type))
      }
    </div>
  );
};

export default MmamaEntityFormGroup;
